import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  IonApp,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonToggle,
  MenuController,
} from '@ionic/angular/standalone';
import { AuthSessionService } from './core/services/auth-session.service';
import { FlavorService } from './core/services/flavor.service';
import { MemberSessionService } from './core/services/member-session.service';
import { BiometricAuthService } from './core/plugins/biometric-auth.service';
import { environment } from 'src/environments/environment';
import { TelemetryService } from './core/services/telemetry.service';
import { UiPreviewService } from './core/services/ui-preview.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    CommonModule,
    IonApp,
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenu,
    IonMenuToggle,
    IonRouterOutlet,
    IonToggle,
  ],
})
export class AppComponent implements OnInit {
  readonly flavor = this.flavorService.config;
  readonly activeSession = this.memberSession.session;
  readonly isAuthenticated = computed(() => !!this.activeSession());
  readonly memberDisplayName = computed(() => {
    const member = this.activeSession()?.member;
    if (!member) {
      return '';
    }

    return `${member.name ?? ''} ${member.surname ?? ''}`.trim();
  });
  readonly memberCardNumber = computed(
    () => this.activeSession()?.cardNumber ?? null
  );
  readonly version = environment.appVersion;
  readonly biometricsAvailable = signal(false);
  readonly biometricLoginEnabled = signal(this.biometricAuth.isBiometricLoginEnabled());
  readonly showBiometricToggle = computed(() => Capacitor.isNativePlatform() && this.biometricsAvailable());

  constructor(
    private readonly router: Router,
    private readonly menuController: MenuController,
    private readonly flavorService: FlavorService,
    private readonly authSession: AuthSessionService,
    private readonly memberSession: MemberSessionService,
    private readonly biometricAuth: BiometricAuthService,
    private readonly telemetry: TelemetryService,
    private readonly uiPreview: UiPreviewService
  ) {
    this.flavorService.initialize();
  }

  ngOnInit(): void {
    this.uiPreview.initialize();
    this.telemetry.initialize();
    void this.initializeBiometricMenuState();
    void this.enforceBiometricGateOnLaunch();
  }

  async onBiometricToggleChanged(event: CustomEvent<{ checked: boolean }>): Promise<void> {
    const enabled = !!event.detail?.checked;
    this.biometricAuth.setBiometricLoginEnabled(enabled);
    this.biometricLoginEnabled.set(enabled);
    await this.menuController.close('main-menu');
  }

  async navigateTo(path: string): Promise<void> {
    await this.menuController.close('main-menu');
    await this.router.navigate([path]);
  }

  async callSupport(): Promise<void> {
    await this.menuController.close('main-menu');
    window.location.href = `tel:${this.flavor.supportNumber}`;
  }

  async logout(): Promise<void> {
    this.biometricAuth.clearStoredCredentials();
    this.authSession.clearPendingSession();
    this.memberSession.clearSession();
    await this.telemetry.setUserIdentifier(null);
    await this.menuController.close('main-menu');
    await this.router.navigate(['/card-number'], { replaceUrl: true });
  }

  private async enforceBiometricGateOnLaunch(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (!this.biometricAuth.hasStoredCredentials()) {
      return;
    }

    if (!this.biometricAuth.isBiometricLoginEnabled()) {
      return;
    }

    this.biometricAuth.requestAutoPromptOnNextCardPage();

    if (!this.memberSession.getSession()) {
      if (this.router.url !== '/card-number') {
        await this.router.navigate(['/card-number'], { replaceUrl: true });
      }
      return;
    }

    this.authSession.clearPendingSession();
    this.memberSession.clearSession();
    await this.router.navigate(['/card-number'], { replaceUrl: true });
  }

  private async initializeBiometricMenuState(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.biometricsAvailable.set(false);
      return;
    }

    this.biometricsAvailable.set(await this.biometricAuth.isAvailable());
    this.biometricLoginEnabled.set(this.biometricAuth.isBiometricLoginEnabled());
  }
}
